/**
 * Financial Diagnostic Wizard Screen
 * Multi-step wizard for collecting financial diagnostic data
 */

import React, { useEffect, useRef, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
  ActivityIndicator,
  TouchableOpacity,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { colors } from '../theme/colors';
import { typography } from '../theme/typography';
import { spacing } from '../theme/spacing';
import logger from '../utils/logger';
import { useFinancialDiagnosticStore } from '../store/financialDiagnosticStore';
import { useProfileStore } from '../store/profileStore';
import CurrencyInput from '../components/financialDiagnostic/CurrencyInput';
import CardInput, { CardTypeOption } from '../components/financialDiagnostic/CardInput';
import DebtCardInput, { DebtTypeOption } from '../components/financialDiagnostic/DebtCardInput';
import SavingsCardInput, { SavingsTypeOption } from '../components/financialDiagnostic/SavingsCardInput';
import Button from '../components/common/Button';
import ErrorMessage from '../components/common/ErrorMessage';

// Type options for revenue breakdown
const revenueTypeOptions: CardTypeOption[] = [
  { value: 'salary', label: 'Salaire' },
  { value: 'business', label: 'Activité' },
  { value: 'investment', label: 'Investissements' },
  { value: 'rental', label: 'Loyers' },
  { value: 'pension', label: 'Pension' },
  { value: 'other', label: 'Autre' },
];

// Type options for expense breakdown
const expenseTypeOptions: CardTypeOption[] = [
  { value: 'housing', label: 'Logement' },
  { value: 'food', label: 'Alimentation' },
  { value: 'transport', label: 'Transport' },
  { value: 'insurance', label: 'Assurances' },
  { value: 'subscriptions', label: 'Abonnements' },
  { value: 'leisure', label: 'Loisirs' },
  { value: 'health', label: 'Santé' },
  { value: 'taxes', label: 'Impôts' },
  { value: 'education', label: 'Éducation' },
  { value: 'family_assistance', label: 'Aide familiale' },
  { value: 'children_expenses', label: 'Enfants' },
  { value: 'other', label: 'Autre' },
];

// Type options for debt breakdown (using DebtTypeOption for card layout)
const debtTypeOptions: DebtTypeOption[] = [
  { value: 'mortgage', label: 'Crédit immobilier' },
  { value: 'consumer', label: 'Crédit consommation' },
  { value: 'student', label: 'Prêt étudiant' },
  { value: 'auto', label: 'Crédit auto' },
  { value: 'credit_card', label: 'Carte de crédit' },
  { value: 'overdraft', label: 'Découvert bancaire' },
  { value: 'personal', label: 'Prêt personnel' },
  { value: 'other', label: 'Autre dette' },
];

// Default savings type options (French - will be overridden by API if available)
const defaultSavingsTypeOptions: SavingsTypeOption[] = [
  { value: 'livret_a', label: 'Livret A' },
  { value: 'ldds', label: 'LDDS' },
  { value: 'lep', label: 'LEP' },
  { value: 'pel', label: 'PEL' },
  { value: 'assurance_vie', label: 'Assurance-vie' },
  { value: 'pea', label: 'PEA' },
  { value: 'per', label: 'PER' },
  { value: 'pee', label: 'PEE' },
  { value: 'checking_account', label: 'Compte courant' },
  { value: 'savings_account', label: 'Compte épargne' },
  { value: 'crypto', label: 'Cryptomonnaies' },
  { value: 'investment_account', label: 'Compte titre' },
  { value: 'other', label: 'Autre' },
];

export default function FinancialDiagnosticWizardScreen() {
  const navigation = useNavigation();

  const {
    currentStep,
    totalSteps,
    currentQuestion,
    formData,
    diagnostic,
    isStarting,
    isSubmitting,
    isComplete,
    error,
    startWizard,
    submitStep,
    goToPreviousStep,
    clearError,
    reset,
    // Breakdown state - always shown by default
    incomeBreakdown,
    expensesBreakdown,
    debtBreakdown,
    savingsBreakdown,
    setIncomeBreakdown,
    setExpensesBreakdown,
    setDebtBreakdown,
    setSavingsBreakdown,
    savingsTypesData,
  } = useFinancialDiagnosticStore();

  const { setHasProfile } = useProfileStore();
  const scrollViewRef = useRef<ScrollView>(null);

  // Scroll to top when step changes
  useEffect(() => {
    scrollViewRef.current?.scrollTo({ y: 0, animated: true });
  }, [currentStep]);

  // Get savings type options from API or use defaults
  const savingsTypeOptions: SavingsTypeOption[] = React.useMemo(() => {
    if (savingsTypesData?.savings_types) {
      return Object.entries(savingsTypesData.savings_types).map(([value, label]) => ({
        value,
        label,
      }));
    }
    return defaultSavingsTypeOptions;
  }, [savingsTypesData]);

  // Local state for current field value
  const [currentValue, setCurrentValue] = useState<number | undefined>(undefined);

  // Start wizard on mount - reset first to clear any stale state
  useEffect(() => {
    reset();
    startWizard();
  }, []);

  // Navigate back to message when complete - with scroll to diagnostic
  useEffect(() => {
    logger.log('[FinancialDiagnosticWizardScreen] useEffect - isComplete:', isComplete, 'diagnostic:', diagnostic);
    if (isComplete && diagnostic) {
      logger.log('[FinancialDiagnosticWizardScreen] ✅ Navigating to Chats tab...');

      // Update profileStore to indicate user has a profile
      // This ensures the Quick Actions panel shows "Mettre à jour" instead of "Commencer"
      setHasProfile(true);

      // Wizard complete - diagnostic has been saved to message
      // Navigate back to MainContent with Chats tab active
      (navigation as any).navigate('MainContent', {
        initialRoute: 'Chats',
        scrollToDiagnostic: true,
        diagnosticTimestamp: Date.now(), // Force refresh
      });
    }
  }, [isComplete, diagnostic, navigation, setHasProfile]);

  // Update current value when question changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (currentQuestion) {
      const field = currentQuestion.field as keyof typeof formData;
      const savedValue = formData[field] as number | undefined;
      setCurrentValue(savedValue);
    }
  }, [currentQuestion]); // Only re-run when question changes, not on every formData update

  const handleNext = async () => {
    // Prevent double submission
    if (isSubmitting || !currentQuestion) return;

    const field = currentQuestion.field;

    // Always calculate from breakdown for income, expenses, debt, savings
    let valueToSubmit = currentValue;
    if (field === 'monthly_income') {
      valueToSubmit = incomeBreakdown.reduce((sum, item) => sum + (item.amount || 0), 0);
    } else if (field === 'monthly_expenses') {
      valueToSubmit = expensesBreakdown.reduce((sum, item) => sum + (item.amount || 0), 0);
    } else if (field === 'debt_amount') {
      valueToSubmit = debtBreakdown.reduce((sum, item) => sum + (item.amount || 0), 0);
    } else if (field === 'current_savings') {
      valueToSubmit = savingsBreakdown.reduce((sum, item) => sum + (item.amount || 0), 0);
    }

    const data = { [field]: valueToSubmit };

    await submitStep(data);
  };

  const handlePrevious = () => {
    goToPreviousStep();
  };

  const handleSkip = async () => {
    // Prevent double submission
    if (isSubmitting || !currentQuestion) return;

    const field = currentQuestion.field;
    const data = { [field]: 0 }; // Submit 0 for skipped fields

    await submitStep(data);
  };

  // Calculate if user can proceed - always use breakdown totals for income, expenses, debt, savings
  const getEffectiveValue = (): number | undefined => {
    if (currentQuestion?.field === 'monthly_income') {
      const total = incomeBreakdown.reduce((sum, item) => sum + (item.amount || 0), 0);
      return total > 0 ? total : undefined;
    }
    if (currentQuestion?.field === 'monthly_expenses') {
      const total = expensesBreakdown.reduce((sum, item) => sum + (item.amount || 0), 0);
      return total > 0 ? total : undefined;
    }
    if (currentQuestion?.field === 'debt_amount') {
      const total = debtBreakdown.reduce((sum, item) => sum + (item.amount || 0), 0);
      return total; // Allow 0 for debt
    }
    if (currentQuestion?.field === 'current_savings') {
      const total = savingsBreakdown.reduce((sum, item) => sum + (item.amount || 0), 0);
      return total; // Allow 0 for savings (though unusual)
    }
    return currentValue;
  };

  const effectiveValue = getEffectiveValue();
  const canProceed = effectiveValue !== undefined && effectiveValue !== null;
  const canGoBack = currentStep > 1;

  if (isStarting) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
        <Text style={styles.loadingText}>Chargement du wizard...</Text>
      </View>
    );
  }

  if (!currentQuestion && !isComplete) {
    return (
      <View style={styles.loadingContainer}>
        <Text style={styles.errorTitle}>Erreur</Text>
        <Text style={styles.errorText}>
          Impossible de charger les questions. Veuillez réessayer.
        </Text>
        <Button
          title="Retour"
          onPress={() => navigation.goBack()}
          variant="outline"
        />
      </View>
    );
  }

  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      style={styles.container}
    >
      <ScrollView
        ref={scrollViewRef}
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
      >
        {/* Header */}
        <View style={styles.header}>
          <TouchableOpacity
            onPress={() => navigation.goBack()}
            style={styles.closeButton}
          >
            <Text style={styles.closeButtonText}>✕</Text>
          </TouchableOpacity>

          <Text style={styles.headerTitle}>État des lieux financier</Text>

          {/* Progress indicator */}
          <View style={styles.progressContainer}>
            <View style={styles.progressBar}>
              <View
                style={[
                  styles.progressFill,
                  {
                    width: `${(currentStep / totalSteps) * 100}%`,
                  },
                ]}
              />
            </View>
            <Text style={styles.progressText}>
              Étape {currentStep} sur {totalSteps}
            </Text>
          </View>
        </View>

        {/* Question */}
        {currentQuestion && (
          <View style={styles.content}>
            {/* Question label */}
            <Text style={styles.questionLabel}>{currentQuestion.label}</Text>
            {currentQuestion.help_text && (
              <Text style={styles.questionHelpText}>{currentQuestion.help_text}</Text>
            )}

            {/* Step 1: Monthly Income - card layout for mobile */}
            {currentQuestion.field === 'monthly_income' && (
              <CardInput
                items={incomeBreakdown}
                onChangeItems={(items) => setIncomeBreakdown(items as any)}
                typeOptions={revenueTypeOptions}
                maxLines={100}
                unit="€"
                addButtonLabel="+ Ajouter un revenu"
                totalLabel="Total revenus"
                autoRevealCount={3}
              />
            )}

            {/* Step 2: Monthly Expenses - card layout for mobile */}
            {currentQuestion.field === 'monthly_expenses' && (
              <CardInput
                items={expensesBreakdown}
                onChangeItems={(items) => setExpensesBreakdown(items as any)}
                typeOptions={expenseTypeOptions}
                maxLines={100}
                unit="€"
                addButtonLabel="+ Ajouter une dépense"
                totalLabel="Total dépenses"
                autoRevealCount={4}
              />
            )}

            {/* Step 3: Debt Amount - mobile-friendly card layout */}
            {currentQuestion.field === 'debt_amount' && (
              <DebtCardInput
                items={debtBreakdown}
                onChangeItems={(items) => setDebtBreakdown(items as any)}
                typeOptions={debtTypeOptions}
                maxLines={100}
                autoRevealCount={2}
              />
            )}

            {/* Step 4: Current Savings - mobile-friendly card layout */}
            {currentQuestion.field === 'current_savings' && (
              <SavingsCardInput
                items={savingsBreakdown}
                onChangeItems={(items) => setSavingsBreakdown(items as any)}
                typeOptions={savingsTypeOptions}
                maxLines={100}
                autoRevealCount={3}
              />
            )}

            {/* Other steps: simple CurrencyInput */}
            {currentQuestion.field !== 'monthly_income' &&
              currentQuestion.field !== 'monthly_expenses' &&
              currentQuestion.field !== 'debt_amount' &&
              currentQuestion.field !== 'current_savings' && (
                <CurrencyInput
                  label=""
                  value={currentValue}
                  onChangeValue={setCurrentValue}
                  placeholder={currentQuestion.placeholder}
                  unit={currentQuestion.unit}
                />
              )}

            {error && (
              <ErrorMessage
                error={error}
                onDismiss={clearError}
              />
            )}

            {/* Action buttons */}
            <View style={styles.buttonContainer}>
              <View style={styles.navigationButtons}>
                {canGoBack && (
                  <View style={styles.buttonHalf}>
                    <Button
                      title="Précédent"
                      onPress={handlePrevious}
                      variant="outline"
                      disabled={isSubmitting}
                    />
                  </View>
                )}
                <View style={[styles.buttonHalf, !canGoBack && styles.buttonFull]}>
                  <Button
                    title={currentStep === totalSteps ? 'Obtenir mon diagnostic' : 'Suivant'}
                    onPress={handleNext}
                    loading={isSubmitting}
                    disabled={!canProceed || isSubmitting}
                  />
                </View>
              </View>

              {currentQuestion.field === 'debt_amount' && (
                <TouchableOpacity
                  onPress={handleSkip}
                  style={styles.skipButton}
                  disabled={isSubmitting}
                >
                  <Text style={styles.skipButtonText}>
                    Je n'ai pas de dettes (0€)
                  </Text>
                </TouchableOpacity>
              )}
            </View>

            {/* Helper text */}
            <View style={styles.helperContainer}>
              <Text style={styles.helperIcon}>💡</Text>
              <Text style={styles.helperText}>
                {currentQuestion.field === 'monthly_income' &&
                  'Inclus tous tes revenus (salaire, primes, revenus secondaires...)'}
                {currentQuestion.field === 'monthly_expenses' &&
                  'Estime tes dépenses mensuelles moyennes (loyer, courses, transport...)'}
                {currentQuestion.field === 'debt_amount' &&
                  'Somme totale de tes dettes (crédit immo, crédit conso, découvert...)'}
                {currentQuestion.field === 'current_savings' &&
                  'Épargne totale disponible (livrets, comptes épargne, placements...)'}
                {currentQuestion.field === 'monthly_saving_capacity' &&
                  'Montant que tu peux mettre de côté chaque mois en moyenne'}
              </Text>
            </View>
          </View>
        )}
      </ScrollView>
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  scrollContent: {
    flexGrow: 1,
    paddingBottom: spacing.xl,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.lg,
    backgroundColor: colors.background,
  },
  loadingText: {
    marginTop: spacing.md,
    fontSize: typography.fontSize.md,
    color: colors.textSecondary,
  },
  errorTitle: {
    fontSize: typography.fontSize.xl,
    fontWeight: typography.fontWeight.bold,
    color: colors.error,
    marginBottom: spacing.sm,
  },
  errorText: {
    fontSize: typography.fontSize.md,
    color: colors.textSecondary,
    textAlign: 'center',
    marginBottom: spacing.lg,
  },
  header: {
    backgroundColor: colors.backgroundSecondary,
    paddingTop: Platform.OS === 'ios' ? 60 : spacing.xl,
    paddingHorizontal: spacing.lg,
    paddingBottom: spacing.lg,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  closeButton: {
    alignSelf: 'flex-end',
    padding: spacing.xs,
  },
  closeButtonText: {
    fontSize: typography.fontSize.xl,
    color: colors.textSecondary,
  },
  headerTitle: {
    fontSize: typography.fontSize.xxl,
    fontWeight: typography.fontWeight.bold,
    color: colors.text,
    marginBottom: spacing.md,
  },
  progressContainer: {
    marginTop: spacing.sm,
  },
  progressBar: {
    height: 4,
    backgroundColor: colors.backgroundTertiary,
    borderRadius: 2,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    backgroundColor: colors.primary,
  },
  progressText: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    marginTop: spacing.xs,
  },
  content: {
    flex: 1,
    padding: spacing.lg,
  },
  questionLabel: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.semibold,
    color: colors.text,
    marginBottom: spacing.sm,
  },
  questionHelpText: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    marginBottom: spacing.md,
  },
  buttonContainer: {
    marginTop: spacing.xl,
  },
  navigationButtons: {
    flexDirection: 'row',
    gap: spacing.sm,
  },
  buttonHalf: {
    flex: 1,
  },
  buttonFull: {
    flex: 1,
  },
  skipButton: {
    marginTop: spacing.md,
    paddingVertical: spacing.sm,
    alignItems: 'center',
  },
  skipButtonText: {
    fontSize: typography.fontSize.sm,
    color: colors.primary,
    textDecorationLine: 'underline',
  },
  helperContainer: {
    marginTop: spacing.xl,
    flexDirection: 'row',
    backgroundColor: colors.backgroundSecondary,
    borderRadius: 8,
    padding: spacing.md,
  },
  helperIcon: {
    fontSize: typography.fontSize.lg,
    marginRight: spacing.sm,
  },
  helperText: {
    flex: 1,
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    lineHeight: typography.lineHeight.relaxed * typography.fontSize.sm,
  },
});
